import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import { withStyles } from '@material-ui/core/styles';

import OcrIngestionContainer from '../../containers/ocr/OcrIngestion';
import Transition from './Transition';

const styles = () => ({
	dialogPaper: {
		maxWidth: '95vw',
		width: '95vw',
		height: '95vh',
		maxHeight: '95vh',
		margin: '10px',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	titleBar: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '8px 16px',
		borderBottom: '1px solid #ddd',
		flexShrink: 0,
	},
	body: {
		display: 'flex',
		flex: 1,
		overflow: 'hidden',
	},
	leftPane: {
		flex: 1,
		overflowY: 'auto',
		padding: '0 16px 16px 16px',
	},
	rightPane: {
		width: '45%',
		flexShrink: 0,
		borderLeft: '2px solid #ddd',
		display: 'flex',
		flexDirection: 'column',
	},
	rightPaneLabel: {
		padding: '6px 12px',
		fontSize: 11,
		fontWeight: 'bold',
		color: '#888',
		background: '#f9f9f9',
		borderBottom: '1px solid #ddd',
		flexShrink: 0,
	},
	pdfEmbed: {
		flex: 1,
		width: '100%',
		border: 'none',
	},
	section: {
		borderTop: '1px solid #ddd',
		marginTop: 14,
		paddingTop: 10,
	},
	sectionHeader: {
		fontWeight: 'bold',
		fontSize: 13,
		marginBottom: 6,
		color: '#333',
	},
	jsonBlock: {
		background: '#f5f5f5',
		border: '1px solid #e0e0e0',
		borderRadius: 3,
		padding: '6px 8px',
		fontSize: 11,
		overflowX: 'auto',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
		margin: '3px 0 6px 0',
		fontFamily: 'monospace',
	},
	matchRow: {
		marginBottom: 4,
		fontSize: 12,
	},
	statusMatched: {
		color: '#2E7D32',
		fontWeight: 'bold',
	},
	statusBad: {
		color: '#C62828',
		fontWeight: 'bold',
	},
	statusNeutral: {
		color: '#555',
	},
});

const MATCH_FIELD_GROUPS = [
	{
		statusKey: 'matchStationDeliveryStatus',
		messageKey: 'matchStationDeliveryStatusMessage',
		idKey: 'matchedStationDeliveryId',
		label: 'Station Delivery',
	},
	{
		statusKey: 'matchBulkDeliveryStatus',
		messageKey: 'matchBulkDeliveryStatusMessage',
		idKey: 'matchedBulkDeliveryId',
		label: 'Bulk Delivery',
	},
	{
		statusKey: 'matchStationInvoiceStatus',
		messageKey: 'matchStationInvoiceStatusMessage',
		idKey: 'matchedStationInvoiceId',
		label: 'Station Invoice',
	},
	{
		statusKey: 'matchRackTransferStatus',
		messageKey: 'matchRackTransferStatusMessage',
		idKey: 'matchedRackTransferId',
		label: 'Rack Transfer',
	},
	{
		statusKey: 'matchDataStatus',
		messageKey: 'matchDataStatusMessage',
		label: 'Data Match',
	},
];

class OcrIngestionDebugDialog extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isLoading: false,
			debugData: null,
			promptText: null,
			isLoadingPrompt: false,
		};
	}

	componentDidUpdate(prevProps) {
		const { open, ocrIngestionId } = this.props;
		if (open && ocrIngestionId && (!prevProps.open || ocrIngestionId !== prevProps.ocrIngestionId)) {
			this.fetchDebugData();
		}
	}

	fetchDebugData = () => {
		const { ocrIngestionId, ocrIngestionGetDebug, showErrors } = this.props;
		this.setState({ isLoading: true, debugData: null, promptText: null });
		ocrIngestionGetDebug({
			params: { ocrIngestionId },
			callback: (err, data) => {
				if (err) {
					this.setState({ isLoading: false });
					showErrors(err);
					return;
				}
				this.setState({ isLoading: false, debugData: data });
			},
		});
	};

	handleLoadPrompt = () => {
		const { ocrIngestionGetAiV2Prompt, showErrors } = this.props;
		this.setState({ isLoadingPrompt: true });
		ocrIngestionGetAiV2Prompt({
			callback: (err, data) => {
				if (err) {
					this.setState({ isLoadingPrompt: false });
					showErrors(err);
					return;
				}
				this.setState({ isLoadingPrompt: false, promptText: data.promptText });
			},
		});
	};

	handleClose = () => {
		this.props.onClose();
	};

	getStatusClass(status) {
		const { classes } = this.props;
		if (status === 'matched') {
			return classes.statusMatched;
		}
		if (status === 'notFound' || status === 'error' || status === 'notNumeric') {
			return classes.statusBad;
		}
		return classes.statusNeutral;
	}

	renderIngestionType(ocrIngestion) {
		if (ocrIngestion.isUploadingRackTransfer) {
			return 'Rack Transfer';
		}
		if (ocrIngestion.isUploadingInvoiceV2) {
			return 'Invoice V2';
		}
		return 'Invoice';
	}

	renderResult(result) {
		const { classes } = this.props;
		return (
			<div key={result.ocrIngestionResultId} className={classes.section}>
				<div className={classes.sectionHeader}>Result #{result.ocrIngestionResultId}</div>

				{MATCH_FIELD_GROUPS.map(({ statusKey, messageKey, idKey, label }) => {
					const status = result[statusKey];
					if (!status) {
						return null;
					}
					return (
						<div key={statusKey} className={classes.matchRow}>
							<span style={{ color: '#555' }}>{label}: </span>
							<span className={this.getStatusClass(status)}>{status}</span>
							{idKey && result[idKey] && (
								<span style={{ marginLeft: 10, color: '#333' }}>
									→ ID {result[idKey]}
								</span>
							)}
							{result[messageKey] && (
								<div style={{ marginLeft: 16, color: '#666', fontSize: 11, marginTop: 2 }}>
									{result[messageKey]}
								</div>
							)}
						</div>
					);
				})}

				{result.matchDataExtractedDocs && (
					<div style={{ marginTop: 6 }}>
						<div style={{ fontWeight: 'bold', fontSize: 11, marginBottom: 3 }}>
							matchDataExtractedDocs:
						</div>
						<pre className={classes.jsonBlock}>
							{JSON.stringify(result.matchDataExtractedDocs, null, 2)}
						</pre>
					</div>
				)}
			</div>
		);
	}

	renderLeftPane() {
		const { classes } = this.props;
		const { isLoading, debugData, promptText, isLoadingPrompt } = this.state;

		if (isLoading) {
			return (
				<div style={{ padding: 40, textAlign: 'center' }}>
					<CircularProgress />
					<div style={{ marginTop: 10, color: '#555' }}>Loading debug data...</div>
				</div>
			);
		}

		if (!debugData) {
			return null;
		}

		const { ocrIngestion, ocrIngestionDocuments = [], ocrIngestionResults = [] } = debugData;

		return (
			<>
				{ocrIngestion && (
					<div className={classes.section}>
						<div className={classes.sectionHeader}>Ingestion</div>
						<div className={classes.matchRow}>
							<span style={{ color: '#555' }}>Status: </span>
							<span className={this.getStatusClass(ocrIngestion.ingestionStatus)}>
								{ocrIngestion.ingestionStatus}
							</span>
							{ocrIngestion.ingestionStatusMessage && (
								<span style={{ marginLeft: 10, color: '#555', fontSize: 11 }}>
									{ocrIngestion.ingestionStatusMessage}
								</span>
							)}
						</div>
						<div className={classes.matchRow}>
							<span style={{ color: '#555' }}>Type: </span>
							{this.renderIngestionType(ocrIngestion)}
						</div>
						<div className={classes.matchRow}>
							<span style={{ color: '#555' }}>Created: </span>
							{ocrIngestion.dateCreated}
						</div>
					</div>
				)}

				{ocrIngestionDocuments.map((doc) => (
					<div key={doc.ocrIngestionDocumentId} className={classes.section}>
						<div className={classes.sectionHeader}>
							Page {doc.pageNumber}
							<span
								style={{ marginLeft: 10, fontWeight: 'normal' }}
								className={this.getStatusClass(doc.ingestionStatus)}
							>
								{doc.ingestionStatus}
							</span>
							{doc.ingestionStatusMessage && (
								<span
									style={{
										marginLeft: 8,
										fontWeight: 'normal',
										color: '#555',
										fontSize: 11,
									}}
								>
									{doc.ingestionStatusMessage}
								</span>
							)}
						</div>

						{doc.matchedOcrGuideId && (
							<div className={classes.matchRow}>
								<span style={{ color: '#555' }}>Matched Guide: </span>
								{doc.matchedOcrGuideId}
							</div>
						)}

						<div style={{ fontWeight: 'bold', fontSize: 11, marginBottom: 2 }}>AI Data:</div>
						<pre className={classes.jsonBlock}>
							{doc.aiData ? JSON.stringify(doc.aiData, null, 2) : '(none)'}
						</pre>

						<div style={{ fontWeight: 'bold', fontSize: 11, marginBottom: 2 }}>
							Extracted Data:
						</div>
						<pre className={classes.jsonBlock}>
							{doc.extractedData ? JSON.stringify(doc.extractedData, null, 2) : '(none)'}
						</pre>
					</div>
				))}

				{ocrIngestionResults.map((result) => this.renderResult(result))}

				<div className={classes.section}>
					<div className={classes.sectionHeader}>AI V2 Prompt</div>
					{!promptText && (
						<Button
							variant="outlined"
							size="small"
							onClick={this.handleLoadPrompt}
							disabled={isLoadingPrompt}
						>
							{isLoadingPrompt ? (
								<>
									<CircularProgress size={14} style={{ marginRight: 8 }} />
									Loading...
								</>
							) : (
								'Load V2 Prompt'
							)}
						</Button>
					)}
					{promptText && <pre className={classes.jsonBlock}>{promptText}</pre>}
				</div>
			</>
		);
	}

	renderRightPane() {
		const { classes, resourceMap } = this.props;
		const { debugData } = this.state;

		const documentResourceId = _.get(debugData, ['ocrIngestion', 'documentResourceId']);
		const resource = documentResourceId ? resourceMap[documentResourceId] : null;
		const pdfUrl = resource ? resource.url : null;

		return (
			<div className={classes.rightPane}>
				<div className={classes.rightPaneLabel}>ORIGINAL PDF</div>
				{pdfUrl ? (
					<object
						data={pdfUrl}
						type="application/pdf"
						className={classes.pdfEmbed}
					>
						<a href={pdfUrl}>Open PDF</a>
					</object>
				) : (
					<div
						style={{
							flex: 1,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: '#aaa',
							fontSize: 13,
						}}
					>
						{debugData ? 'PDF not available' : 'Load debug data to view PDF'}
					</div>
				)}
			</div>
		);
	}

	render() {
		const { classes, fullScreen, open, ocrIngestionId } = this.props;

		return (
			<Dialog
				fullScreen={fullScreen}
				maxWidth={false}
				open={open}
				TransitionComponent={Transition}
				onClose={this.handleClose}
				classes={{ paper: classes.dialogPaper }}
			>
				<div className={classes.titleBar}>
					<DialogTitle style={{ padding: 0 }}>
						Debug — OCR Ingestion #{ocrIngestionId}
					</DialogTitle>
					<Button variant="contained" size="small" onClick={this.handleClose}>
						Close
					</Button>
				</div>

				<div className={classes.body}>
					<div className={classes.leftPane}>{this.renderLeftPane()}</div>
					{this.renderRightPane()}
				</div>
			</Dialog>
		);
	}
}

OcrIngestionDebugDialog.propTypes = {
	classes: PropTypes.object.isRequired,
	fullScreen: PropTypes.bool.isRequired,
	open: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	ocrIngestionId: PropTypes.number,
	resourceMap: PropTypes.object,
	showErrors: PropTypes.func,
	ocrIngestionGetDebug: PropTypes.func,
	ocrIngestionGetAiV2Prompt: PropTypes.func,
};

export default _.flow([withStyles(styles), withMobileDialog(), OcrIngestionContainer])(
	OcrIngestionDebugDialog
);
